import { differenceInMinutes, startOfDay } from 'date-fns';
import { ChevronRight } from 'lucide-react';

import { CountryFlag } from '@/components/country-flag';
import { Link } from '@/components/link';
import { ModeIcon } from '@/components/mode-icon';
import { PortTooltip } from '@/components/port-tooltip';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { VehicleDelayStatus } from '@/components/vehicle-delay-status';
import { VisuallyHidden } from '@/components/visually-hidden';
import { DATE_FORMAT, DELAY_STATUS, ISO_DATE_TIME_FORMAT } from '@/lib/constants';
import { SimpleScheduleRoute } from '@/types';
import { formatDuration, formatToLocationDateTime } from '@/utils/format';
import { createPortPageUrl, getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { createScheduleSearchPageUrl } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';
import { getVehicleDelay, getVehicleName } from '@/utils/vehicle';

interface Props {
  row: SimpleScheduleRoute;
  timeRange: number;
  rangeStartTime: Date;
}

const SimpleScheduleRow = ({ row, timeRange, rangeStartTime }: Props) => {
  // Destructure Data
  const { mode, departureDateTime, arrivalDateTime, duration, from, to, distanceKm, vehicle, carrierId } = row;

  const fromPortIdentifier = getPreferredIdentifier(from.identifiers);
  const toPortIdentifier = getPreferredIdentifier(to.identifiers);

  const departureDateTimeAsDate = new Date(departureDateTime);
  const arrivalDateTimeAsDate = new Date(arrivalDateTime);

  // Get Timezones
  const departureTimezone = from.timezoneName;
  const arrivalTimezone = to.timezoneName;

  // Departure/Arrival Time
  const departureString = formatToLocationDateTime(departureDateTimeAsDate, departureTimezone, DATE_FORMAT);
  const arrivalString = formatToLocationDateTime(arrivalDateTimeAsDate, arrivalTimezone, DATE_FORMAT);
  const departureIsoDateTime = formatToLocationDateTime(
    departureDateTimeAsDate,
    departureTimezone,
    ISO_DATE_TIME_FORMAT,
  );
  const arrivalIsoDateTime = formatToLocationDateTime(arrivalDateTimeAsDate, arrivalTimezone, ISO_DATE_TIME_FORMAT);

  // Vehicle
  const vehicleName = vehicle ? getVehicleName(vehicle) : '';
  const vehicleCode = getPreferredIdentifier(vehicle?.identifiers);
  const vehicleDelay = vehicle ? getVehicleDelay(vehicle, departureDateTime) : undefined;
  const vehicleModeColor = getTransportColor(mode);

  // Determine how to to draw the bars
  const scheduleDurationInHrs = duration / 60 / 60;
  const schedulePercent = (scheduleDurationInHrs / timeRange) * 100;
  const scheduleOffset = differenceInMinutes(departureDateTimeAsDate, rangeStartTime);
  const scheduleOffsetPercent = (scheduleOffset / 60 / timeRange) * 100;

  // Delay info
  const isDelayed = vehicleDelay?.status === DELAY_STATUS.DELAYED;
  const delayString = isDelayed
    ? ` Based on latest tracking updates, this vessel might be delayed by ${vehicleDelay?.days} days`
    : ` Based on latest tracking updates, this vessel appears to be on time.`;

  // generates a row description string
  const getRowDescription = () => {
    return `${vehicleName} departs ${from.name} (${fromPortIdentifier?.value}) on ${departureString} and arrives in ${
      to.name
    } (${toPortIdentifier?.value}) on ${arrivalString}. The transit time between these ports is ${formatDuration(
      duration,
    )} and is a direct service with no stops.${delayString}`;
  };

  // Build url for row, ensuring date is used from schedule
  const dateFrom = startOfDay(new Date(departureDateTime));
  const scheduleUrl = createScheduleSearchPageUrl({
    origin: from,
    destination: to,
    mode,
    dateRange: { dateFrom },
    filters: { carrierIds: [carrierId] },
  });

  const handleRowClick = () => {
    trackUser.event('Carrier Schedules: Row Clicked', {
      carrierId,
    });
  };

  const {
    tr,
    td,
    cell,
    primaryText,
    secondaryText,
    underlinedText,
    arrow,
    modeBar,
    segmentBar,
    segmentFill,
    segmentSpacer,
    segmentIcon,
    scheduleLink,
  } = styles();

  return (
    <tr className={tr()}>
      <td className={td()}>
        <div className={cell({ className: 'border-none' })}>
          <Text className={primaryText()}>
            <CountryFlag countryCode={from.countryCode} className="mr-2 h-4" />
            <Tooltip content={<PortTooltip location={from} mode={mode} />} followCursor="horizontal">
              {fromPortIdentifier ? (
                <Link
                  href={createPortPageUrl([fromPortIdentifier])}
                  className={underlinedText({ className: 'font-semiBold' })}
                  target="_blank"
                >
                  {fromPortIdentifier.value}
                </Link>
              ) : null}
            </Tooltip>
          </Text>
          <time className={secondaryText()} dateTime={departureIsoDateTime}>
            {departureString}
          </time>
          <span className={arrow()}>→</span>
        </div>
      </td>
      <td className={td()}>
        <div className={cell({ className: 'border-none' })}>
          <Text className={primaryText()}>
            <CountryFlag countryCode={to.countryCode} className="mr-2 h-4" />
            <Tooltip content={<PortTooltip location={to} mode={mode} />} followCursor="horizontal">
              {toPortIdentifier ? (
                <Link
                  href={createPortPageUrl([toPortIdentifier])}
                  className={underlinedText({ className: 'font-semiBold' })}
                  target="_blank"
                >
                  {toPortIdentifier.value}
                </Link>
              ) : null}
            </Tooltip>
          </Text>
          <time className={secondaryText()} dateTime={arrivalIsoDateTime}>
            {arrivalString}
          </time>
        </div>
      </td>
      <td className={td()}>
        <div className={cell()}>
          <div className={primaryText()}>
            <Tooltip content={<div className="max-w-60 p-1">{getRowDescription()}</div>} followCursor="horizontal">
              <span className={underlinedText()}>{formatDuration(duration)}</span>
            </Tooltip>
            <div className="ml-2">
              <VehicleDelayStatus delay={vehicleDelay} />
            </div>
          </div>

          <Text className={secondaryText()}>{distanceKm || 'Direct service'}</Text>
        </div>
      </td>
      <td className={td()}>
        <div className={cell()}>
          <Text className={primaryText()}>{vehicleName}</Text>
          <Text className={secondaryText()}>IMO {vehicleCode?.value || ''}</Text>
        </div>
      </td>
      <td className={td({ className: 'hidden md:table-cell' })}>
        <div className={cell({ className: 'border-none pr-6' })}>
          <VisuallyHidden as="p">{getRowDescription()}</VisuallyHidden>
          <div className={modeBar()}>
            <div className={segmentSpacer()} style={{ width: `${scheduleOffsetPercent}%` }} />
            <div className={segmentBar()} style={{ width: `${schedulePercent}%` }}>
              <span className={segmentIcon()}>
                <ModeIcon mode={mode} size="sm" />
              </span>
              <div className={segmentFill()} style={{ background: vehicleModeColor }} />
            </div>
          </div>
        </div>
      </td>
      <td className={td({ className: 'hidden md:table-cell' })}>
        <div className={cell()}>
          <VisuallyHidden as="p">See sailing schedules</VisuallyHidden>
          <Tooltip content="See schedules for this service" followCursor="horizontal">
            <Link href={scheduleUrl} onClick={handleRowClick} className={scheduleLink()}>
              <ChevronRight size={20} />
            </Link>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

const styles = tv({
  slots: {
    tr: 'relative mb-2 grid grid-cols-2 rounded-md border border-grey-200 bg-white leading-snug md:m-0 md:table-row md:border-none',
    td: 'h-full py-2 first:rounded-l-md first:border-l first:border-grey-200 last:rounded-r-md last:border-r last:border-grey-200 md:border-y md:border-grey-200 [&:nth-last-child(3)]:border-t [&:nth-last-child(3)]:border-grey-200 [&:nth-last-child(4)]:border-t [&:nth-last-child(4)]:border-grey-200',
    cell: 'relative flex flex-col justify-center px-4 py-3 pr-0 md:border-l md:border-grey-200',
    primaryText: 'm-0 flex min-h-[18px] items-center text-baseSm font-medium',
    secondaryText: 'm-0 mt-1 text-xs',
    underlinedText: 'cursor-pointer border-b border-dashed border-grey-300 hover:border-solid hover:border-grey-800',
    arrow: 'absolute right-2 top-3 text-base text-grey-500',
    modeBar: 'relative z-8 -mx-3 flex h-5 w-full items-center rounded-[10px] bg-grey-100 px-3',
    segmentBar: 'relative z-2 h-5 min-w-8',
    segmentFill: 'h-5 w-full rounded-[10px] opacity-60',
    segmentIcon: 'absolute left-0 top-0 z-2',
    segmentSpacer: 'block',
    scheduleLink: 'grid h-8 w-8 place-items-center rounded-md bg-grey-100 hover:bg-lightBlue-500 hover:text-white',
  },
});

export { SimpleScheduleRow };
